// Supabase client (Phase 10 — replaces Emergent OAuth).
//
// Auth uses Implicit flow (`flowType: 'implicit'`) because ShipDesi often runs
// inside an Emergent platform iframe where partitioned localStorage drops the
// PKCE code_verifier after OAuth redirect. Session is persisted in
// localStorage; the axios interceptor reads the access token from
// `supabase.auth.getSession()` on every request.

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const RECOVERY_PENDING_KEY = 'shipdesi_recovery_pending';
const RECOVERY_ERROR_KEY = 'shipdesi_recovery_error';

/**
 * Snapshot recovery intent from the URL before createClient runs detectSessionInUrl
 * (which strips the hash). AuthContext imports this module on app boot, before
 * lazy-loaded ResetPassword mounts.
 */
function snapshotRecoveryFromUrlIfNeeded() {
  if (typeof window === 'undefined') return;
  if (!window.location.pathname.startsWith('/reset-password')) return;
  try {
    const hashSp = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    const searchSp = new URLSearchParams(window.location.search || '');

    if (hashSp.has('error') || searchSp.has('error')) {
      sessionStorage.setItem(RECOVERY_ERROR_KEY, JSON.stringify({
        error_code: hashSp.get('error_code') || searchSp.get('error_code') || '',
        error_description: hashSp.get('error_description') || searchSp.get('error_description') || '',
      }));
      return;
    }

    const type = hashSp.get('type') || searchSp.get('type');
    const isRecovery = type === 'recovery'
      || hashSp.has('access_token')
      || searchSp.has('code')
      || hashSp.has('token_hash')
      || searchSp.has('token_hash');
    if (isRecovery) {
      sessionStorage.setItem(RECOVERY_PENDING_KEY, '1');
    }
  } catch { /* ignore */ }
}

snapshotRecoveryFromUrlIfNeeded();

/** True when this page load arrived via a password-recovery redirect (snapshotted pre-client). */
export function isRecoveryPending() {
  try { return sessionStorage.getItem(RECOVERY_PENDING_KEY) === '1'; } catch { return false; }
}

export function clearRecoveryPending() {
  try {
    sessionStorage.removeItem(RECOVERY_PENDING_KEY);
    sessionStorage.removeItem(RECOVERY_ERROR_KEY);
  } catch { /* ignore */ }
}

/** Read and clear a Supabase auth error returned in the URL hash (e.g. otp_expired). */
export function consumeRecoveryUrlError() {
  try {
    const raw = sessionStorage.getItem(RECOVERY_ERROR_KEY);
    sessionStorage.removeItem(RECOVERY_ERROR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const desc = (parsed.error_description || '').replace(/\+/g, ' ').trim();
    const code = parsed.error_code || '';
    if (desc) return desc;
    if (code === 'otp_expired') return 'This reset link has expired or was already used. Please request a new one.';
    return null;
  } catch {
    return null;
  }
}

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // Don't crash — the rest of the app still works in legacy cookie mode.
  // eslint-disable-next-line no-console
  console.warn('[supabase] REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY missing — Google OAuth disabled');
}

export const supabase = SUPABASE_URL && SUPABASE_ANON_KEY
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        // Phase 10 fix: switched from 'pkce' to 'implicit'.
        // PKCE stores the code_verifier in localStorage during
        // signInWithOAuth and reads it back during exchangeCodeForSession
        // on the callback page. Modern browsers (Chrome 113+) partition
        // localStorage per top-level frame; when the SPA is hosted inside
        // a platform iframe (Emergent's emergent-main.js wrapper),
        // localStorage written in the iframe context is invisible after
        // the OAuth top-level redirect lands back. That manifests as
        // `AuthPKCECodeVerifierMissingError` and the access_token never
        // arrives. Implicit flow returns the access_token directly in
        // the URL fragment, so no cross-context storage is required.
        flowType: 'implicit',
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storageKey: 'shipdesi.supabase.auth',
      },
    })
  : null;

export async function getSupabaseAccessToken() {
  if (!supabase) return null;
  try {
    // Playwright mock auth: prefer the injected localStorage session so gotrue
    // refresh against placeholder.supabase.co cannot clear the token mid-probe.
    if (typeof window !== 'undefined' && window.__SHIPDESI_E2E_MOCK_AUTH) {
      try {
        const raw = localStorage.getItem('shipdesi.supabase.auth');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.access_token) return parsed.access_token;
        }
      } catch { /* fall through */ }
    }
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch {
    return null;
  }
}

export async function signInWithGoogle({ redirectTo } = {}) {
  if (!supabase) throw new Error('Supabase is not configured');
  const dest = redirectTo || `${window.location.origin}/auth/callback`;
  // eslint-disable-next-line no-console
  console.info('[supabase] signInWithGoogle redirectTo=', dest);
  const result = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: dest,
      queryParams: { prompt: 'select_account' },
    },
  });
  if (result?.data?.url) {
    // eslint-disable-next-line no-console
    console.info('[supabase] OAuth provider URL=', result.data.url);
  }
  if (result?.error) {
    // eslint-disable-next-line no-console
    console.error('[supabase] signInWithOAuth error:', result.error);
  }
  return result;
}

export async function signOutSupabase() {
  if (!supabase) return;
  try {
    await supabase.auth.signOut();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[supabase] signOut failed, clearing local session:', e);
    try { localStorage.removeItem('shipdesi.supabase.auth'); } catch { /* ignore */ }
  }
}

/** Revoke every session for the user across all devices (Supabase global sign-out). */
export async function signOutEverywhere() {
  if (!supabase) return;
  try {
    await supabase.auth.signOut({ scope: 'global' });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[supabase] global signOut failed, clearing local session:', e);
    try { localStorage.removeItem('shipdesi.supabase.auth'); } catch { /* ignore */ }
  }
}

// ── Email / password auth (Phase 11 — replaces custom /auth/register + /auth/login) ──

/**
 * Sign up with email + password. With "Confirm email" ON in Supabase, this
 * returns a user but NO session until the confirmation link is clicked. The
 * confirmation email links back to `${origin}/auth/callback`.
 */
export async function signUpWithEmail({ email, password, name }) {
  if (!supabase) throw new Error('Supabase is not configured');
  return supabase.auth.signUp({
    email,
    password,
    options: {
      data: name ? { full_name: name, name } : undefined,
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });
}

export async function signInWithEmail({ email, password }) {
  if (!supabase) throw new Error('Supabase is not configured');
  return supabase.auth.signInWithPassword({ email, password });
}

/** Send a password-reset email. The link lands on `${origin}/reset-password`. */
export async function sendPasswordReset(email) {
  if (!supabase) throw new Error('Supabase is not configured');
  return supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
}

/** Set a new password for the user in an active recovery session. */
export async function updatePassword(password) {
  if (!supabase) throw new Error('Supabase is not configured');
  return supabase.auth.updateUser({ password });
}

/** Resend the sign-up confirmation email for an unconfirmed account. */
export async function resendConfirmationEmail(email) {
  if (!supabase) throw new Error('Supabase is not configured');
  return supabase.auth.resend({
    type: 'signup',
    email,
    options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
  });
}

/**
 * Map a supabase-js AuthError into a friendly, user-facing message. Handles the
 * common cases plus rate limiting (HTTP 429).
 */
export function friendlyAuthError(error) {
  if (!error) return '';
  const status = error.status || error?.originalError?.status;
  const msg = (error.message || '').toLowerCase();
  if (status === 429 || msg.includes('rate limit') || msg.includes('too many')) {
    return 'Too many attempts. Please wait a minute and try again.';
  }
  if (msg.includes('email not confirmed')) {
    return 'Please confirm your email first. Check your inbox for the verification link.';
  }
  if (msg.includes('invalid login credentials')) {
    return 'Invalid email or password.';
  }
  if (msg.includes('user already registered') || msg.includes('already been registered')) {
    return 'An account with this email already exists. Try signing in.';
  }
  if (msg.includes('password') && msg.includes('should be at least')) {
    return error.message;
  }
  if (msg.includes('invalid') && (msg.includes('totp') || msg.includes('code') || msg.includes('factor'))) {
    return 'Invalid authenticator code. Please try again.';
  }
  return error.message || 'Something went wrong. Please try again.';
}

// ── Optional TOTP MFA (Supabase Auth) ─────────────────────────────────────────
// MFA is never required at registration. Users enable/disable from Profile → Security.

/** Playwright MFA harness — set by tests/e2e/helpers/auth.ts (no GoTrue HTTP). */
function e2eMfaState() {
  if (typeof window === 'undefined') return null;
  if (!window.__SHIPDESI_E2E_MOCK_AUTH || !window.__SHIPDESI_E2E_MFA) return null;
  if (!window.__SHIPDESI_E2E_MFA_STATE) {
    window.__SHIPDESI_E2E_MFA_STATE = {
      factorId: 'factor_e2e',
      verified: false,
      enrolledPending: false,
      currentAal: 'aal2',
      sessionTokenAfterVerify: null,
    };
  }
  return window.__SHIPDESI_E2E_MFA_STATE;
}

function e2eTotpFactor(state, status) {
  const now = new Date().toISOString();
  return {
    id: state.factorId,
    friendly_name: 'ShipDesi Authenticator',
    factor_type: 'totp',
    status,
    created_at: now,
    updated_at: now,
  };
}

export async function listMfaFactors() {
  const state = e2eMfaState();
  if (state) {
    const all = [];
    if (state.verified) all.push(e2eTotpFactor(state, 'verified'));
    else if (state.enrolledPending) all.push(e2eTotpFactor(state, 'unverified'));
    // Match supabase-js shape: totp lists all TOTP factors (verified + pending).
    return { data: { all, totp: all, phone: [], webauthn: [] }, error: null };
  }
  if (!supabase) throw new Error('Supabase is not configured');
  return supabase.auth.mfa.listFactors();
}

export async function enrollTotpMfa({ friendlyName } = {}) {
  const state = e2eMfaState();
  if (state) {
    state.enrolledPending = true;
    state.verified = false;
    return {
      data: {
        id: state.factorId,
        type: 'totp',
        friendly_name: friendlyName || 'ShipDesi Authenticator',
        totp: {
          qr_code:
            'data:image/svg+xml,' +
            encodeURIComponent(
              '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120"><rect width="120" height="120" fill="#fff"/><text x="10" y="60" font-size="12">MFA QR</text></svg>',
            ),
          secret: 'JBSWY3DPEHPK3PXP',
          uri: 'otpauth://totp/ShipDesi:test',
        },
      },
      error: null,
    };
  }
  if (!supabase) throw new Error('Supabase is not configured');
  return supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: friendlyName || 'ShipDesi Authenticator',
  });
}

export async function challengeAndVerifyMfa({ factorId, code }) {
  const state = e2eMfaState();
  if (state) {
    const digits = String(code || '').trim();
    if (digits !== '123456') {
      return { data: null, error: { message: 'Invalid TOTP code' } };
    }
    state.verified = true;
    state.enrolledPending = false;
    state.currentAal = 'aal2';
    if (state.sessionTokenAfterVerify && typeof localStorage !== 'undefined') {
      try {
        const key = 'shipdesi.supabase.auth';
        const raw = localStorage.getItem(key);
        if (raw) {
          const session = JSON.parse(raw);
          session.access_token = state.sessionTokenAfterVerify;
          session.user = session.user || {};
          session.user.factors = [e2eTotpFactor(state, 'verified')];
          session.user.aal = 'aal2';
          localStorage.setItem(key, JSON.stringify(session));
        }
      } catch { /* ignore */ }
    }
    return {
      data: {
        access_token: state.sessionTokenAfterVerify || 'mock-aal2-token',
        user: { id: factorId, factors: [e2eTotpFactor(state, 'verified')] },
      },
      error: null,
    };
  }
  if (!supabase) throw new Error('Supabase is not configured');
  const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
    factorId,
  });
  if (challengeError) return { data: null, error: challengeError };
  return supabase.auth.mfa.verify({
    factorId,
    challengeId: challengeData.id,
    code: String(code || '').trim(),
  });
}

export async function unenrollMfa({ factorId }) {
  const state = e2eMfaState();
  if (state) {
    state.verified = false;
    state.enrolledPending = false;
    return { data: { id: factorId }, error: null };
  }
  if (!supabase) throw new Error('Supabase is not configured');
  return supabase.auth.mfa.unenroll({ factorId });
}

export async function getMfaAssuranceLevel() {
  if (!supabase) {
    return { data: { currentLevel: null, nextLevel: null }, error: null };
  }
  // E2E mock sessions skip real AAL unless the MFA harness opts in.
  if (typeof window !== 'undefined' && window.__SHIPDESI_E2E_MOCK_AUTH && !window.__SHIPDESI_E2E_MFA) {
    return { data: { currentLevel: 'aal1', nextLevel: 'aal1' }, error: null };
  }
  const state = e2eMfaState();
  if (state) {
    const currentLevel = state.currentAal || 'aal1';
    const nextLevel = state.verified ? 'aal2' : currentLevel;
    return { data: { currentLevel, nextLevel, currentAuthenticationMethods: [] }, error: null };
  }
  return supabase.auth.mfa.getAuthenticatorAssuranceLevel();
}

export async function refreshAuthSession() {
  if (e2eMfaState()) {
    return { data: { session: null }, error: null };
  }
  if (!supabase) return { data: { session: null }, error: null };
  return supabase.auth.refreshSession();
}

/** True when the user has MFA enrolled but this session is still AAL1. */
export function needsMfaChallenge(assurance) {
  const current = assurance?.currentLevel;
  const next = assurance?.nextLevel;
  return current === 'aal1' && next === 'aal2';
}
